export const BACKGROUND_MUSIC_URL =
  "/media/sky-e4ccb944.mp3";

export const GAME_AUDIO_MIX = {
  music: 0.2,
  master: 0.72,
  effects: 1,
  ambience: 0.18,
  pausedAmbience: 0.02,
  glideAmbience: 0.34,
  wind: 0.62,
  glideWind: 1.05,
  windFrequency: 620,
  glideWindFrequency: 1250,
  shardPeak: 0.22,
  corePeak: 0.32,
  respawnPeak: 0.2,
} as const;

export interface BackgroundMusicState {
  src: string;
  loop: boolean;
  paused: boolean;
  ended: boolean;
  volume: number;
  currentTime: number;
  duration: number;
  readyState: number;
  error: string;
}

interface LegacyAudioWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

export class GameAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private effects: GainNode | null = null;
  private ambience: GainNode | null = null;
  private musicSource: MediaElementAudioSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private windGain: GainNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private music: HTMLAudioElement | null = null;
  private enabled = true;
  private paused = true;
  private gliding = false;
  private hasStarted = false;
  private musicDuckUntil = 0;
  private musicDuckFactor = 1;
  private backgroundMusicError = "";
  private webAudioError = "";

  get isEnabled(): boolean {
    return this.enabled;
  }

  get webAudioState(): string {
    return this.context?.state ?? "uninitialized";
  }

  get webAudioErrorMessage(): string {
    return this.webAudioError;
  }

  get backgroundMusicState(): BackgroundMusicState | null {
    if (!this.music) return null;
    return {
      src: this.music.currentSrc || this.music.src,
      loop: this.music.loop,
      paused: this.music.paused,
      ended: this.music.ended,
      volume: this.musicGain?.gain.value ?? this.music.volume,
      currentTime: this.music.currentTime,
      duration: this.music.duration,
      readyState: this.music.readyState,
      error: this.backgroundMusicError,
    };
  }

  async start(): Promise<void> {
    if (!this.enabled) return;
    this.hasStarted = true;

    if (!this.music) {
      try {
        this.createBackgroundMusic();
      } catch (error) {
        this.backgroundMusicError = this.describeError(error, "無法建立背景音樂播放器");
      }
    }

    let resumePromise: Promise<void> = Promise.resolve();
    try {
      if (!this.context || !this.master || !this.effects || !this.ambience) {
        this.createAudioGraph();
      }
      resumePromise = this.resumeWebAudioFromGesture();
    } catch (error) {
      this.webAudioError = this.describeError(error, "手機音效引擎初始化失敗");
    }

    const musicPromise = !this.paused
      ? this.playBackgroundMusic()
      : Promise.resolve();
    this.master?.gain.setTargetAtTime(
      this.enabled ? GAME_AUDIO_MIX.master : 0,
      this.context?.currentTime ?? 0,
      0.02,
    );
    this.updateAmbienceMix();
    const [resumeResult] = await Promise.allSettled([resumePromise, musicPromise]);

    if (resumeResult.status === "rejected") {
      this.webAudioError = this.describeError(resumeResult.reason, "手機瀏覽器拒絕啟用遊戲音效");
    } else if (this.context?.state === "running") {
      this.webAudioError = "";
    }

    try {
      this.master?.gain.setTargetAtTime(
        this.enabled ? GAME_AUDIO_MIX.master : 0,
        this.context?.currentTime ?? 0,
        0.12,
      );
      this.updateAmbienceMix();
    } catch {
      // 下一次玩家點擊、按鍵或觸碰時會再次嘗試。
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.context && this.master) {
      this.master.gain.setTargetAtTime(
        enabled ? GAME_AUDIO_MIX.master : 0,
        this.context.currentTime,
        0.08,
      );
    }
    if (!enabled) {
      this.music?.pause();
    } else if (this.hasStarted && !this.paused) {
      void this.start();
    }
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    this.updateAmbienceMix();
    if (paused) {
      this.music?.pause();
    } else if (this.enabled && this.hasStarted) {
      void this.start();
    }
  }

  setGliding(gliding: boolean): void {
    if (this.gliding === gliding) return;
    this.gliding = gliding;
    this.updateAmbienceMix();
  }

  playShard(): void {
    if (!this.enabled || !this.context || !this.effects) return;
    const now = this.context.currentTime;
    this.duckMusic(0.45, 0.68);
    [659.25, 830.61, 987.77].forEach((frequency, index) => {
      const oscillator = this.context!.createOscillator();
      const gain = this.context!.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, now + index * 0.065);
      gain.gain.setValueAtTime(0, now + index * 0.065);
      gain.gain.linearRampToValueAtTime(
        GAME_AUDIO_MIX.shardPeak,
        now + index * 0.065 + 0.02,
      );
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.65 + index * 0.065);
      oscillator.connect(gain).connect(this.effects!);
      oscillator.start(now + index * 0.065);
      oscillator.stop(now + 0.72 + index * 0.065);
    });
  }

  playCore(): void {
    if (!this.enabled || !this.context || !this.effects) return;
    const now = this.context.currentTime;
    this.duckMusic(0.34, 1.85);
    const oscillator = this.context.createOscillator();
    const overtone = this.context.createOscillator();
    const gain = this.context.createGain();
    const filter = this.context.createBiquadFilter();
    oscillator.type = "sine";
    overtone.type = "triangle";
    oscillator.frequency.setValueAtTime(110, now);
    oscillator.frequency.exponentialRampToValueAtTime(220, now + 1.25);
    overtone.frequency.setValueAtTime(440, now);
    overtone.frequency.exponentialRampToValueAtTime(659.25, now + 1.1);
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(900, now);
    filter.frequency.exponentialRampToValueAtTime(3400, now + 0.9);
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(GAME_AUDIO_MIX.corePeak, now + 0.16);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.8);
    oscillator.connect(filter);
    overtone.connect(filter);
    filter.connect(gain).connect(this.effects);
    oscillator.start(now);
    overtone.start(now);
    oscillator.stop(now + 1.9);
    overtone.stop(now + 1.9);
  }

  playRespawn(): void {
    if (!this.enabled || !this.context || !this.effects) return;
    const now = this.context.currentTime;
    this.duckMusic(0.48, 0.82);
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(440, now);
    oscillator.frequency.exponentialRampToValueAtTime(165, now + 0.7);
    gain.gain.setValueAtTime(GAME_AUDIO_MIX.respawnPeak, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.75);
    oscillator.connect(gain).connect(this.effects);
    oscillator.start(now);
    oscillator.stop(now + 0.8);
  }

  private createAudioGraph(): void {
    const AudioContextClass = window.AudioContext
      || (window as LegacyAudioWindow).webkitAudioContext;
    if (!AudioContextClass) throw new Error("這個瀏覽器不支援 Web Audio");

    const context = new AudioContextClass();
    try {
      const master = context.createGain();
      const effects = context.createGain();
      const ambience = context.createGain();
      master.gain.value = 0;
      effects.gain.value = GAME_AUDIO_MIX.effects;
      ambience.gain.value = GAME_AUDIO_MIX.ambience;
      effects.connect(master);
      ambience.connect(master);
      master.connect(context.destination);

      const length = context.sampleRate * 4;
      const buffer = context.createBuffer(1, length, context.sampleRate);
      const channel = buffer.getChannelData(0);
      let last = 0;
      for (let index = 0; index < length; index += 1) {
        const white = Math.random() * 2 - 1;
        last = last * 0.985 + white * 0.015;
        channel[index] = last * 1.8;
      }

      const wind = context.createBufferSource();
      const windFilter = context.createBiquadFilter();
      const windGain = context.createGain();
      wind.buffer = buffer;
      wind.loop = true;
      windFilter.type = "bandpass";
      windFilter.frequency.value = GAME_AUDIO_MIX.windFrequency;
      windFilter.Q.value = 0.36;
      windGain.gain.value = GAME_AUDIO_MIX.wind;
      wind.connect(windFilter).connect(windGain).connect(ambience);

      const drone = context.createOscillator();
      const droneGain = context.createGain();
      drone.type = "sine";
      drone.frequency.value = 110;
      droneGain.gain.value = 0.025;
      drone.connect(droneGain).connect(ambience);

      wind.start();
      drone.start();

      let musicSource: MediaElementAudioSourceNode | null = null;
      let musicGain: GainNode | null = null;
      if (this.music) {
        try {
          musicSource = context.createMediaElementSource(this.music);
          musicGain = context.createGain();
          musicGain.gain.value = GAME_AUDIO_MIX.music;
          musicSource.connect(musicGain).connect(master);
          this.music.volume = 1;
        } catch (error) {
          this.webAudioError = this.describeError(
            error,
            "背景音樂將使用手機原生播放器，遊戲音效仍可正常播放",
          );
        }
      }

      this.context = context;
      this.master = master;
      this.effects = effects;
      this.ambience = ambience;
      this.musicSource = musicSource;
      this.musicGain = musicGain;
      this.windGain = windGain;
      this.windFilter = windFilter;
    } catch (error) {
      void context.close().catch(() => {
        // 建立失敗時清理半成品，下一次觸碰可以完整重試。
      });
      throw error;
    }
  }

  private createBackgroundMusic(): void {
    const music = new Audio(BACKGROUND_MUSIC_URL);
    music.loop = true;
    music.preload = "metadata";
    music.volume = GAME_AUDIO_MIX.music;
    music.addEventListener("playing", () => {
      this.backgroundMusicError = "";
    });
    music.addEventListener("error", () => {
      this.backgroundMusicError = music.error?.message || "音樂檔載入失敗";
    });
    this.music = music;
  }

  private async playBackgroundMusic(): Promise<void> {
    if (!this.music || !this.enabled || this.paused || !this.music.paused) return;
    try {
      await this.music.play();
    } catch (error) {
      this.backgroundMusicError = error instanceof Error
        ? `${error.name}: ${error.message}`
        : "瀏覽器拒絕播放背景音樂";
      // 自動播放政策或暫時性網路錯誤不應中斷遊戲；下次操作會再次嘗試。
    }
  }

  private resumeWebAudioFromGesture(): Promise<void> {
    const context = this.context;
    if (!context || context.state === "running" || context.state === "closed") {
      return Promise.resolve();
    }

    try {
      const unlock = context.createBufferSource();
      unlock.buffer = context.createBuffer(1, 1, context.sampleRate);
      unlock.connect(context.destination);
      unlock.start(0);
    } catch {
      // 某些手機瀏覽器不需要靜音緩衝；仍繼續呼叫 resume。
    }
    return context.resume();
  }

  private updateAmbienceMix(): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    const isActiveGlide = this.gliding && !this.paused;
    const ambienceTarget = this.paused
      ? GAME_AUDIO_MIX.pausedAmbience
      : isActiveGlide
        ? GAME_AUDIO_MIX.glideAmbience
        : GAME_AUDIO_MIX.ambience;
    this.ambience?.gain.setTargetAtTime(ambienceTarget, now, 0.16);
    this.windGain?.gain.setTargetAtTime(
      isActiveGlide ? GAME_AUDIO_MIX.glideWind : GAME_AUDIO_MIX.wind,
      now,
      0.18,
    );
    this.windFilter?.frequency.setTargetAtTime(
      isActiveGlide ? GAME_AUDIO_MIX.glideWindFrequency : GAME_AUDIO_MIX.windFrequency,
      now,
      0.2,
    );
  }

  private duckMusic(target: number, duration: number): void {
    if (!this.context || !this.musicGain) return;
    const now = this.context.currentTime;
    const releaseAt = Math.max(this.musicDuckUntil, now + duration);
    const factor = now < this.musicDuckUntil
      ? Math.min(this.musicDuckFactor, target)
      : target;
    this.musicDuckUntil = releaseAt;
    this.musicDuckFactor = factor;
    const gain = this.musicGain.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    gain.setTargetAtTime(GAME_AUDIO_MIX.music * factor, now, 0.035);
    gain.setTargetAtTime(GAME_AUDIO_MIX.music, releaseAt, 0.16);
  }

  private describeError(error: unknown, fallback: string): string {
    return error instanceof Error ? `${error.name}: ${error.message}` : fallback;
  }
}
